import { BehaviorSubject, Subject } from 'rxjs';
import {
    Directive,
    ViewContainerRef,
    TemplateRef,
    Input,
    TrackByFunction,
    IterableDiffers,
    OnInit,
    OnDestroy,
    SimpleChanges,
    NgIterable,
} from '@angular/core';
import { FilterMethod, QuickListService, defaultQLSettings } from './quick-list.service';
import { NgForOf, NgForOfContext } from '@angular/common';
import { Platform } from '@angular/cdk/platform';

@Directive({ selector: '[sklDynamicList]' })
export class DynamicListDirective<T, U extends NgIterable<T> = NgIterable<T>> extends NgForOf<T, U> implements OnInit, OnDestroy {
    @Input() public set sklDynamicListOf(filterMethod: FilterMethod<T>) {
        this.qlService = this.getQuickListService(filterMethod);
    }

    @Input('sklDynamicListTrackBy') public set trackBy(trackBy: TrackByFunction<T>) {
        super.ngForTrackBy = trackBy;
    }

    @Input('sklDynamicListSearch') public pattern?: string;

    private static MOBILE_PAGE_SIZE = 20;
    public items$ = new BehaviorSubject<T[]>([]);
    public isLoading$ = new BehaviorSubject(false);
    public itemsPerPage$ = new BehaviorSubject(0);
    private onSearchChange$ = new Subject<string>();
    private qlService!: QuickListService;
    private currentPattern = defaultQLSettings.filterSettings.pattern;
    private pageSize = this.isMobile ? DynamicListDirective.MOBILE_PAGE_SIZE :defaultQLSettings.paginationSettings.pageSize;
    private dynamicList: T[] = [];
    private currentPage = 0;

    public constructor(
        public viewContainerRef: ViewContainerRef,
        public platform: Platform,
        public templateRef: TemplateRef<NgForOfContext<T, U>>,
        public differs: IterableDiffers,
    ) {
        super(viewContainerRef, templateRef, differs);
    }

    public async ngOnInit(): Promise<void> {
        this.onSearchChange$.subscribe(text => this.search(text));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        changes['pattern'] ? this.onSearchChange$.next(changes['pattern'].currentValue) : this.onSearchChange$.complete();
    }

    public ngOnDestroy(): void {
        this.onSearchChange$.complete();
        this.itemsPerPage$.complete();
        this.items$.complete();
    }

    public async search(value: string): Promise<T[]> {
        this.currentPage = 0;
        this.currentPattern = value;
        const dynamicList = [
            ...(await this.loadItems({
                pattern: this.currentPattern,
                page: this.currentPage,
            })),
        ];
        if (!value) ++this.currentPage;

        return this.updateDynamicList(dynamicList);
    }

    public async loadPage(): Promise<T[]> {
        const previousPage = this.currentPage;
        const nextPage = {
            pattern: this.currentPattern,
            page: this.currentPage++,
        };
        const items = [...(await this.loadItems(nextPage))];
        const newItems = previousPage ? [...this.dynamicList, ...items] : items;

        return this.updateDynamicList(newItems);
    }

    public async loadFirstPage(): Promise<T[]> {
        this.currentPage = 0;
        this.currentPattern = defaultQLSettings.filterSettings.pattern;

        return this.loadPage();
    }

    private getQuickListService(filterMethod: FilterMethod<T>): QuickListService {
        const qlService = new QuickListService();
        qlService.config({
            filterMethod,
            settings: {
                filterSettings: {
                    pattern: defaultQLSettings.filterSettings.pattern,
                },
                paginationSettings: {
                    pageNumber: defaultQLSettings.paginationSettings.pageNumber,
                    pageSize: this.pageSize
                }
            },
        });

        return qlService;
    }

    private async loadItems(
        { pattern, page } = {
            pattern: defaultQLSettings.filterSettings.pattern,
            page: defaultQLSettings.paginationSettings.pageNumber,
        },
    ): Promise<T[]> {
        this.isLoading$.next(true);
        const data = await this.qlService.loadData({ pattern, page });
        this.itemsPerPage$.next(data.length);

        return data;
    }

    private updateDynamicList(data: T[]): T[] {
        this.dynamicList = [...data];
        this.items$.next([...data]);
        super.ngForOf = this.dynamicList as U;
        this.isLoading$.next(false);

        return this.dynamicList;
    }

    private get isMobile(): boolean {
        return this.platform.ANDROID || this.platform.IOS;
    }
}
